import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Calendar, FolderOpen, MessageSquare,
  FileText, Mail, Award, Users, Menu, X, LogOut,
  ChevronDown, Settings, Image
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', permission: 'dashboard' },
  { icon: Calendar, label: 'Training Calendar', path: '/admin/training-calendar', permission: 'training_calendar' },
  { icon: Users, label: 'Registrations', path: '/admin/registrations', permission: 'registrations' },
  { icon: Mail, label: 'Contact Submissions', path: '/admin/contact-submissions', permission: 'contact_submissions' },
  { icon: Settings, label: 'Site Settings', path: '/admin/site-settings', adminOnly: true },
  { icon: Settings, label: 'Email Settings', path: '/admin/email-settings', adminOnly: true },
  { icon: Image, label: 'Gallery', path: '/admin/gallery', permission: 'gallery' },
  { icon: Image, label: 'Customer Logos', path: '/admin/customer-logos', permission: 'customer_logos' },
  { icon: FolderOpen, label: 'Training Categories', path: '/admin/training-categories', adminOnly: true },
  { icon: MessageSquare, label: 'Testimonials', path: '/admin/testimonials', adminOnly: true },
  { icon: FileText, label: 'Blog Posts', path: '/admin/blog-posts', adminOnly: true },
  { icon: Award, label: 'Certificates', path: '/admin/certificates', adminOnly: true },
  { icon: Users, label: 'User Management', path: '/admin/users', adminOnly: true },
];

export const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Helper function to check if user has access to a menu item
  const hasAccess = (item) => {
    // Admin users have access to everything
    if (isAdmin) return true;

    // Items marked as adminOnly are only for admins
    if (item.adminOnly) return false;

    // If item has no permission requirement, it's accessible to all
    if (!item.permission) return true;

    // Check if user has the specific permission
    if (user?.permissions && item.permission) {
      return user.permissions[item.permission] === true;
    }

    return false;
  };

  const filteredMenuItems = menuItems.filter(hasAccess);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarOpen ? 280 : 80 }}
        className="fixed left-0 top-0 h-full bg-gradient-to-b from-blue-900 to-blue-800 text-white z-50 shadow-2xl"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-blue-700">
            <div className="flex items-center gap-3">
              <div className="bg-white p-1.5 rounded-lg flex-shrink-0">
                <img 
                  src="https://customer-assets.emergentagent.com/job_training-hub-107/artifacts/shpkz69x_400%20x%20400%20logo-16.png"
                  alt="Logo"
                  className="h-8 w-auto"
                />
              </div>
              {sidebarOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex-1 min-w-0"
                >
                  <div className="font-bold text-lg truncate">ISDE Admin</div>
                  <div className="text-xs text-blue-200">Management Portal</div>
                </motion.div>
              )}
            </div>
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="space-y-1 px-3">
              {filteredMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <motion.button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    whileHover={{ x: 4 }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-blue-600 shadow-lg'
                        : 'hover:bg-blue-700/50'
                    }`}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    {sidebarOpen && (
                      <span className="text-sm font-medium truncate">{item.label}</span>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </nav>

          {/* User Menu */}
          <div className="p-4 border-t border-blue-700">
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-blue-700/50 transition-all"
              >
                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold">
                    {user?.full_name?.charAt(0) || 'A'}
                  </span>
                </div>
                {sidebarOpen && (
                  <>
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-sm font-medium truncate">{user?.full_name}</div>
                      <div className="text-xs text-blue-200 truncate capitalize">{user?.role}</div>
                    </div>
                    <ChevronDown className="h-4 w-4 flex-shrink-0" />
                  </>
                )}
              </button>

              {userMenuOpen && sidebarOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute bottom-full left-0 right-0 mb-2 bg-white text-gray-900 rounded-lg shadow-xl py-2"
                >
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="text-sm">Logout</span>
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div
        className="transition-all duration-300"
        style={{ marginLeft: sidebarOpen ? 280 : 80 }}
      >
        {/* Top Bar */}
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="flex items-center justify-between px-8 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                {menuItems.find(item => item.path === location.pathname)?.label || 'Admin Panel'}
              </h1>
            </div>

            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              View Website
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
